<template>
  <nav
    v-if="totalPages > 1"
    class="app-pagination"
    aria-label="Pagination"
  >
    <button
      type="button"
      class="app-pagination__nav"
      :class="{ 'app-pagination__nav--disabled': page <= 1 }"
      :disabled="page <= 1"
      aria-label="Previous page"
      @click="goTo(page - 1)"
    >
      Prev
    </button>

    <ol class="app-pagination__pages">
      <li v-for="p in visiblePages" :key="p">
        <span
          v-if="p === page"
          class="app-pagination__page app-pagination__page--current"
          aria-current="page"
        >{{ p }}</span>
        <button
          v-else
          type="button"
          class="app-pagination__page"
          @click="goTo(p)"
        >{{ p }}</button>
      </li>
    </ol>

    <button
      type="button"
      class="app-pagination__nav"
      :class="{ 'app-pagination__nav--disabled': page >= totalPages }"
      :disabled="page >= totalPages"
      aria-label="Next page"
      @click="goTo(page + 1)"
    >
      Next
    </button>
  </nav>
</template>

<script>
/**
 * Sliding window of page links (max 10), centered in its container.
 * Current page sits near the middle of the window when total pages allow.
 */
const MAX_VISIBLE = 10

export function buildVisiblePages (page, totalPages, maxVisible = MAX_VISIBLE) {
  const total = Math.max(0, Number(totalPages) || 0)
  const current = Math.min(Math.max(1, Number(page) || 1), Math.max(1, total))
  if (total <= 0) return []
  if (total <= maxVisible) {
    const all = []
    for (let i = 1; i <= total; i++) all.push(i)
    return all
  }

  // Prefer keeping current near the centre of the window
  let start = current - Math.floor((maxVisible - 1) / 2)
  let end = start + maxVisible - 1
  if (start < 1) {
    start = 1
    end = maxVisible
  }
  if (end > total) {
    end = total
    start = total - maxVisible + 1
  }
  const pages = []
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
}

export default {
  name: 'Pagination',
  props: {
    page: {
      type: Number,
      default: 1
    },
    totalPages: {
      type: Number,
      default: 0
    },
    /** Max page number buttons shown at once (Google-style window). */
    maxVisible: {
      type: Number,
      default: MAX_VISIBLE
    }
  },
  computed: {
    visiblePages () {
      return buildVisiblePages(this.page, this.totalPages, this.maxVisible)
    }
  },
  methods: {
    goTo (next) {
      const total = Math.max(0, Number(this.totalPages) || 0)
      const target = Math.min(Math.max(1, Number(next) || 1), Math.max(1, total))
      if (target === this.page || total <= 1) return
      this.$emit('change', target)
    }
  }
}
</script>

<style scoped>
.app-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  width: 100%;
  margin: 16px 0 8px;
  user-select: none;
}

.app-pagination__pages {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Body Medium (14px) + Primary Blue */
.app-pagination__page {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--color-primary);
  font-family: var(--font-regular);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.25;
  cursor: pointer;
  text-decoration: none;
}

.app-pagination__page:hover {
  text-decoration: underline;
}

/* Body Medium Strong (14px) + Grey Dark on white chip */
.app-pagination__page--current {
  background: var(--color-white);
  color: var(--color-dark-grey);
  font-family: var(--font-semibold);
  font-weight: 600;
  cursor: default;
  text-decoration: none;
}

.app-pagination__page--current:hover {
  text-decoration: none;
}

/* Body Medium (14px) + Primary Blue on white pill */
.app-pagination__nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 56px;
  height: 32px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: var(--color-white);
  color: var(--color-primary);
  font-family: var(--font-regular);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.25;
  cursor: pointer;
}

.app-pagination__nav:hover:not(:disabled) {
  text-decoration: underline;
}

.app-pagination__nav--disabled,
.app-pagination__nav:disabled {
  color: var(--color-mid-grey);
  cursor: default;
  opacity: 0.55;
  text-decoration: none;
}
</style>
